import { 
    Box, 
    Typography, 
    Button, 
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material";
import moment from "moment";

import VisibilityIcon from '@mui/icons-material/Visibility';
import GetAppIcon from '@mui/icons-material/GetApp';

import { useGetWeeklyHoursSummaryQuery } from "../../state/api";

const WeeklyHours = () => {
    const theme = useTheme();
    
    const [selectedWeek, setSelectedWeek] = useState({
        startDate: moment().startOf('week').add(1, 'day').format('YYYY-MM-DD'), // Monday
        endDate: moment().endOf('week').add(1, 'day').format('YYYY-MM-DD') // Sunday
    });
    
    const [openDetailDialog, setOpenDetailDialog] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    
    const { data: weeklyData, isLoading, isError, refetch } = useGetWeeklyHoursSummaryQuery(selectedWeek);

    const handleWeekChange = (field, value) => {
        setSelectedWeek(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleViewDetails = (employee) => {
        setSelectedEmployee(employee);
        setOpenDetailDialog(true);
    };

    const handleExportData = () => {
        if (!weeklyData?.summary) return;
        
        // Create CSV content
        const headers = ['Employee Name', 'Department', 'Total Hours', 'Break Hours', 'Days Worked'];
        const csvContent = [
            headers.join(','),
            ...weeklyData.summary.map(emp => [
                emp.employeeName,
                emp.department || 'N/A',
                emp.totalHours,
                emp.totalBreakTime,
                emp.daysWorked
            ].join(','))
        ].join('\n');
        
        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `weekly-hours-${selectedWeek.startDate}-to-${selectedWeek.endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return <Typography>Loading weekly hours summary...</Typography>;
    }

    if (isError) {
        return <Typography color="error">Error loading weekly hours data.</Typography>;
    }

    return (
        <Box m="1.5rem 2.5rem">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="2rem">
                <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Weekly Hours Summary
                </Typography>
                <Box display="flex" gap="1rem" alignItems="center">
                    <TextField
                        label="Week Start"
                        type="date"
                        value={selectedWeek.startDate}
                        onChange={(e) => handleWeekChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <TextField
                        label="Week End"
                        type="date"
                        value={selectedWeek.endDate}
                        onChange={(e) => handleWeekChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <Button
                        variant="outlined"
                        onClick={() => refetch()}
                        sx={{ color: theme.palette.secondary[100], borderColor: theme.palette.secondary[100] }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<GetAppIcon />}
                        onClick={handleExportData}
                        sx={{
                            backgroundColor: theme.palette.secondary.light,
                            color: theme.palette.background.alt,
                        }}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} mb="2rem">
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                        <CardContent>
                            <Typography variant="h6" color="primary">
                                {weeklyData?.totalEmployees || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Active Employees
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                        <CardContent>
                            <Typography variant="h6" color="secondary">
                                {weeklyData?.grandTotalHours || 0}h
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Total Hours Worked
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                        <CardContent>
                            <Typography variant="h6" color="success.main">
                                {moment(weeklyData?.weekStart).format('MMM DD')} - {moment(weeklyData?.weekEnd).format('MMM DD')}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Week Period
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                        <CardContent>
                            <Typography variant="h6" color="info.main">
                                {weeklyData?.summary ? (weeklyData.summary.reduce((total, emp) => total + emp.totalBreakTime, 0)).toFixed(2) : 0}h
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Total Break Time
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Employee Hours Table */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead
                            sx={{
                                // Target all TableCell components within this TableHead
                                '& .MuiTableCell-root': {
                                  // This applies the bottom border to each cell, creating a continuous line
                                  borderBottom: `1px solid ${theme.palette.secondary.main}`,
                                  // You can also add other shared header styles here for consistency
                                  backgroundColor: theme.palette.primary.light,
                                  fontWeight: 'bold',
                                },
                              }}
                        >
                            <TableRow sx={{ backgroundColor: theme.palette.background.alt }}>
                                <TableCell>Employee</TableCell>
                                <TableCell>Department</TableCell>
                                <TableCell align="right">Total Hours</TableCell>
                                <TableCell align="right">Break Hours</TableCell>
                                <TableCell align="right">Days Worked</TableCell>
                                <TableCell align="right">Avg Hours/Day</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: theme.palette.background.alt }}>
                            {weeklyData?.summary?.map((employee) => (
                                <TableRow key={employee.employeeId} hover>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body1" fontWeight="bold">
                                                {employee.employeeName}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {employee.email}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={employee.department || 'N/A'} 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body1" fontWeight="bold" color="primary">
                                            {employee.totalHours}h
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color="textSecondary">
                                            {employee.totalBreakTime}h
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip 
                                            label={employee.daysWorked} 
                                            size="small" 
                                            color={employee.daysWorked >= 5 ? "success" : "warning"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {employee.daysWorked > 0 ? (employee.totalHours / employee.daysWorked).toFixed(1) : 0}h
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            size="small"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => handleViewDetails(employee)}
                                            sx={{ color: theme.palette.secondary[100] }}
                                        >
                                            Details
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Employee Detail Dialog */}
            <Dialog open={openDetailDialog} onClose={() => setOpenDetailDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedEmployee?.employeeName} - Weekly Details
                </DialogTitle>
                <DialogContent>
                    {selectedEmployee && (
                        <Box>
                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        Department: {selectedEmployee.department || 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        Email: {selectedEmployee.email}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Hours: {selectedEmployee.totalHours}h
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        Break Hours: {selectedEmployee.totalBreakTime}h
                                    </Typography>
                                </Grid>
                            </Grid>
                            
                            <Typography variant="h6" gutterBottom>
                                Daily Breakdown
                            </Typography>
                            
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Clock In</TableCell>
                                            <TableCell>Clock Out</TableCell>
                                            <TableCell align="right">Hours</TableCell>
                                            <TableCell align="right">Break</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedEmployee.clockings?.map((clocking, index) => (
                                            <TableRow key={index} >
                                                <TableCell >
                                                    {moment(clocking.date).format('MMM DD, YYYY')}
                                                </TableCell>
                                                <TableCell>
                                                    {moment(clocking.clockIn).format('HH:mm')}
                                                </TableCell>
                                                <TableCell>
                                                    {clocking.clockOut ? moment(clocking.clockOut).format('HH:mm') : 'N/A'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {clocking.hoursWorked.toFixed(2)}h
                                                </TableCell>
                                                <TableCell align="right">
                                                    {clocking.breakTime.toFixed(2)}h
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={clocking.isAbsent ? 'Absent' : 'Present'} 
                                                        size="small"
                                                        color={clocking.isAbsent ? 'error' : 'success'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDetailDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WeeklyHours; 